import {createSlice, current} from "@reduxjs/toolkit";
import { api, apiErrorsHandler } from "../../api/httpclient";
import { addNotification } from "./notificationsslice";
import { notificationDurationShort, notificationDurationMiddle, notificationDurationLong } from "../../lib/utils";

const defaultDataset = { Id:0, Title: "None", Description: "", CategoryId: 0, DataSources: [], Fields: [], FieldGroups: [], OrderId: -1};
const defaultDatasetId = 0;

const defaultType = {id: 2, title:"Table Data Source"};

const defaultFormInitialValues = {
    Id: 0, 
    DatasetId: defaultDatasetId,
    Title: "",
    Description: "", 
    TypeId: defaultType.id,    
    IsAggregate: false,
    AggregateExpressionId: null,
    AggregateFunctionId: 0,
    FieldId: null,
    Fields: [],
    IsSystem: false,
    OrderId: 0,
    ViewMode: 1
};

const initialState = {
    pageLoaded: false, pageDisposed: true,
    processing: false, processingText: "",
    mode: 0, 
    currentDataset: defaultDataset,
    formKey: (new Date()).toString(), formInitialValues: {...defaultFormInitialValues}, formValues: {...defaultFormInitialValues}, formServerErrors: [], 
    lookups: {
        datasets: []
    },
    
    toolsVisible: true,
    panes: [                    
        {
            // size: "30%",
            size:"20%",
            min: "18%",
            max: "25%",
            collapsible: false
        },           
        {
            //min: "700px",
            //size:"60%",
            min: "53%",
            max: "66%",
            collapsible: false,
        },
        {
            //min: "700px",
            size:"18%",
            min: "16%",
            max: "22%",
            collapsible: false,
        }
      ],
    fieldsDialogOpen: false, fieldsToAdd: [],
    computedFieldDialogOpen: false, computedFieldFieldId: null, computedFieldFunctionId: 0, computedFieldParentFieldId: null, computedFieldMode: 0,
    expressionFieldDialogOpen: false, expressionFieldFieldId: null, expressionFieldDataTypeId: 0, expressionFieldIsDecimal: false, expressionFieldIsPercent: false, expressionFieldBody: "", expressionFieldMode: 0    
};

const dataSourcesPageSlice = createSlice({
    name: "dataSourcesPage",
    initialState: initialState,
    reducers:{
        setInitialState: (state, action) =>  ({...initialState}),//no payload

        setProcessing: (state, action) => {
            //payload: {processing=true, processingText=false}, when processing is false, processingText is "" 
            const {processing=true, processingText=""} = action.payload;

            return ({...state, processing: processing, processingText: processing==false ? "" : processingText});
        },

        setPanes: (state, action) => ({...state, panes: {...action.payload}}),

        setPageValue: (state, action) => ({...state, [action.payload.id]: action.payload.value}),//payload: {id: , value:}

        setFormValue: (state, action) => {
            //payload: {id: fieldId, value: fieldValue}
            let id = action.payload.id;
            let value = action.payload.value;

            return ({
                ...state, 
                formValues: {...state.formValues, [id]: value}
            });
        },

        setFieldValue: (state, action) => {
            //payload is the field - sending a field => replace the field with that id with the passed field's properties
            const pageState = current(state);
            const entity = action.payload;

            return ({...state, formValues: {...state.formValues, Fields: state.formValues.Fields.map(item =>{
                if (item.Id == entity.Id)
                   return {...entity};
                else 
                return item;
            })}});
        },

        setFormServerErrors: (state, action) => ({...state, formServerErrors: [...action.payload]}),

        pageLoaded : (state, action) => {
            //payload: {Datasets: includes the default one  }
            const pageState = current(state);
            if (pageState.pageDisposed)
                return state;

            return {...state, 
                pageLoaded: true,
                processing: false, processingText: "",
                mode: 0, currentDataset: defaultDataset,
                formKey: (new Date()).toString(), formValues:{...defaultFormInitialValues}, formInitialValues:{...defaultFormInitialValues}, formServerErrors:[],            
                lookups:{ 
                    datasets: action.payload.Datasets
                },
            };
        },

        datasetLoaded: (state, action) => {
            //payload: { datasetId: , dataSources: [], fields: [], fieldGroups:[{id: ,Title: , OrderId}]} the array of datasources
            const pageState = current(state);
            if (pageState.pageDisposed)
                return state;
                
            //the data sources, fields and field groups of the dataset need to be refreshed from the api result
            let currentDataset = pageState.lookups.datasets.find (e => e.Id == action.payload.datasetId);
       
            return ({
                ...state,
                processing: false, processingText: "", 
                mode: 0, 
                currentDataset: {...currentDataset, DataSources: [...action.payload.dataSources], Fields: [...action.payload.fields], FieldGroups: [...action.payload.fieldGroups]}, 
                formKey: (new Date()).toString(), formInitialValues: {...defaultFormInitialValues}, formValues: {...defaultFormInitialValues}, formServerErrors: [],
                fieldsDialogOpen: false, fieldsToAdd: [],
                computedFieldDialogOpen: false, computedFieldFieldId: null, computedFieldFunctionId: 0, computedFieldParentFieldId: null, computedFieldMode: 0,
                expressionFieldDialogOpen: false, expressionFieldFieldId: null, expressionFieldDataTypeId: 0, expressionFieldIsDecimal: false, expressionFieldIsPercent: false, expressionFieldBody: "", expressionFieldMode: 0
            });
        },
       
        entityLoaded: (state, action) => {
            //action.payload: the fetched entity
            const pageState = current(state);
            if (pageState.pageDisposed)
                return state;

            return {...state, 
                processing: false, processingText: "",
                mode: 2, 
                formKey: (new Date()).toString(), formInitialValues: {...action.payload, ViewMode: 1}, formValues: {...action.payload, ViewMode: 1}, formServerErrors: [],
                fieldsDialogOpen: false, fieldsToAdd: [],
                computedFieldDialogOpen: false, computedFieldFieldId: null, computedFieldFunctionId: 0, computedFieldParentFieldId: null, computedFieldMode: 0,
                expressionFieldDialogOpen: false, expressionFieldFieldId: null, expressionFieldDataTypeId: 0, expressionFieldIsDecimal: false, expressionFieldIsPercent: false, expressionFieldBody: "", expressionFieldMode: 0
            };
        },

        createEntity: (state, action) => {
            const pageState = current(state);
            const currentDatasetId = pageState.currentDataset.Id;

            return ({
                ...state, 
                mode: 1, 
                formKey: (new Date()).toString(), formInitialValues: {...defaultFormInitialValues, DatasetId: currentDatasetId}, formValues: {...defaultFormInitialValues, DatasetId: currentDatasetId}, formServerErrors: [],
                fieldsDialogOpen: false, fieldsToAdd: [],
                computedFieldDialogOpen: false, computedFieldFieldId: null, computedFieldFunctionId: 0, computedFieldParentFieldId: null, computedFieldMode: 0,
                expressionFieldDialogOpen: false, expressionFieldFieldId: null, expressionFieldDataTypeId: 0, expressionFieldIsDecimal: false, expressionFieldIsPercent: false, expressionFieldBody: "", expressionFieldMode: 0
            });
        }, 
        
        removeEntityFromUI: (state, action) => {
            //payload: the entity id
            let pageState = current(state);
            let entityId = action.payload;
            let newCurrentDataset = {... pageState.currentDataset};

            //compute currentDataset, IF it contains the removed entity, by removing the data source from it            
            if (newCurrentDataset.DataSources.find(e => e.Id == entityId))
                newCurrentDataset = {...newCurrentDataset, DataSources: newCurrentDataset.DataSources.filter(e => e.Id != entityId)};
          
            //if it's the current entity => goto no mode
            let currentMode = {};
            if (pageState.mode == 2 && pageState.formValues.Id == entityId)
               currentMode = {
                mode: 0, 
                formKey: (new Date()).toString(), formInitialValues: {...defaultFormInitialValues}, formValues: {...defaultFormInitialValues}, formServerErrors: [], 
                fieldsDialogOpen: false, fieldsToAdd: [],
                computedFieldDialogOpen: false, computedFieldFieldId: null, computedFieldFunctionId: 0, computedFieldParentFieldId: null, computedFieldMode: 0,
                expressionFieldDialogOpen: false, expressionFieldFieldId: null, expressionFieldDataTypeId: 0, expressionFieldIsDecimal: false, expressionFieldIsPercent: false, expressionFieldBody: "", expressionFieldMode: 0
            };

            return ({...state, currentDataset: newCurrentDataset, ...currentMode});
        },

        entityInserted: (state, action) => {
            //payload: {id:, fields }            
            const pageState = current(state);
            if (pageState.pageDisposed)
                return state;
                
            const id = action.payload.id;
            const fields = action.payload.fields;

            const insertedEntity = {
                ...pageState.formValues, 
                Id: id,
                Fields: pageState.formValues.Fields?.length > 0 ? pageState.formValues.Fields.map(e =>
                {
                    let f = fields.find(x => x.OldId == e.Id);
                    if (!f)
                       return e;
                    else
                       return {...e, Id: f.NewId, ParentFieldId: f.ParentFieldId, DataTypeId: f.DataTypeId, IsDecimal: f.IsDecimal, IsPercent: f.IsPercent};
                }) : pageState.formValues.Fields
            };
             
            let currentDataset = {...pageState.currentDataset};
          
            //add entity to it
            currentDataset = {...currentDataset, DataSources: [...currentDataset.DataSources, insertedEntity]};

            return ({...state, 
                mode: 2, currentDataset: currentDataset, 
                formKey: (new Date()).toString(), formValues: {...insertedEntity}, formInitialValues: {...insertedEntity}, formServerErrors: [],
                fieldsDialogOpen: false, fieldsToAdd:[],
                computedFieldDialogOpen: false, computedFieldFieldId: null, computedFieldFunctionId: 0, computedFieldParentFieldId: null, computedFieldMode: 0,
                expressionFieldDialogOpen: false, expressionFieldFieldId: null, expressionFieldDataTypeId: 0, expressionFieldIsDecimal: false, expressionFieldIsPercent: false, expressionFieldBody: "", expressionFieldMode: 0
            });
        },

        entityUpdated: (state, action) => {
            //payload: {fields }     
            const pageState = current(state);
            if (pageState.pageDisposed)
                return state;

            const entityId = pageState.formValues.Id;        

            const fields = action.payload.fields;
            let entity = pageState.formValues;
            let formValues = {...pageState.formValues};

            if (entity.TypeId == 2 && formValues.Fields?.length > 0)
            {
                //must reload the ids
                formValues.Fields = formValues.Fields.map(e =>
                {
                    let f = fields.find(x => x.OldId == e.Id);
                    if (!f)
                        return e;
                    else
                        return {...e, Id: f.NewId, ParentFieldId: f.ParentFieldId, DataTypeId: f.DataTypeId, IsDecimal: f.IsDecimal, IsPercent: f.IsPercent};
                });
            }

            //rebind in list
            let currentDataset = {...pageState.currentDataset};

            //remove -> add entity to it
            currentDataset = {...currentDataset, DataSources: currentDataset.DataSources.map(item => {
                if (item.Id == entityId)
                   return {...formValues};
                else
                    return item;
            })};


            return ({...state, 
                currentDataset: currentDataset, 
                formKey: (new Date()).toString(), formInitialValues: formValues, formValues: formValues, formServerErrors: [],
                fieldsDialogOpen: false, fieldsToAdd:[],
                computedFieldDialogOpen: false, computedFieldFieldId: null, computedFieldFunctionId: 0, computedFieldParentFieldId: null, computedFieldMode: 0,
                expressionFieldDialogOpen: false, expressionFieldFieldId: null, expressionFieldDataTypeId: 0, expressionFieldIsDecimal: false, expressionFieldIsPercent: false, expressionFieldBody: "", expressionFieldMode: 0
            });
        },

        entityCloned: (state, action) => {
            //payload: {id: , title: , navigate:  }
            const pageState = current(state);

            if (pageState.pageDisposed)
                return state;

            const id = action.payload.id;
            const title = action.payload.title;
            const navigate = action.payload.navigate;

            const insertedEntity = {...pageState.formValues, 
                Id: id, Title: title, OrderId: 0, IsSystem: false
            };
             
            let currentDataset = {...pageState.currentDataset};
          
            //add entity to it
            currentDataset = {...currentDataset, DataSources: [...currentDataset.DataSources, insertedEntity]};

            return ({
                ...state, 
                currentDataset: currentDataset
            });
        },

        entityDeleted: (state, action) => {
            //payload: id of the deleted entity
            const pageState = current(state);
            if (pageState.pageDisposed)
                return state;

            const deletedEntityId = action.payload;
            let isCurrentEntity = false;

            if (pageState.mode == 2 && pageState.formValues.Id == deletedEntityId)
                isCurrentEntity = true;
            
            //remove the entity from currentDataset
            let newCurrentDataset = {...pageState.currentDataset, DataSources: [...pageState.currentDataset.DataSources.filter(e => e.Id != deletedEntityId)]};
            
            let newMode={};
            if(isCurrentEntity)
                newMode={
                    mode: 0, 
                    formKey: (new Date()).toString(), formInitialValues: {...defaultFormInitialValues}, formValues: {...defaultFormInitialValues}, formServerErrors: [],
                    fieldsDialogOpen: false, fieldsToAdd:[],
                    computedFieldDialogOpen: false, computedFieldFieldId: null, computedFieldFunctionId: 0, computedFieldParentFieldId: null, computedFieldMode: 0,
                    expressionFieldDialogOpen: false, expressionFieldFieldId: null, expressionFieldDataTypeId: 0, expressionFieldIsDecimal: false, expressionFieldIsPercent: false, expressionFieldBody: "", expressionFieldMode: 0
                };

            return ({...state, currentDataset:newCurrentDataset, ...newMode});
        },
      
        //non-computed fields dialog
        openFieldsDialog: (state, action) => {
            const pageState = current(state);           

            let fieldsToAdd = pageState.currentDataset
                                .Fields //they are sorted by title since when the dataset is loaded
                                //cannot filter because i'll need to include a fields more than once if i want to apply different agg. fun. on the same field .filter(e => !pageState.formValues.Fields.find(f => f.FieldId == e.Id))
                                .map((item, index) => ({
                                    Id: 0, Title: item.Title, Name: item.Title, IsComputedField: false, 
                                    FunctionId: 0, FieldId: item.Id, IsGrouped: false, ParentFieldId: null, 
                                    ExpressionBody: "", ExpressionBodyResolved: "", DataTypeId: item.DataTypeId, IsDecimal: item.IsDecimal, IsPercent: item.IsPercent, 
                                    HasAggregateFunction: false, HasComputedFunction: false, HasExpressionBody: false,
                                    FieldIsAggregate: item.IsAggregate, FieldIsExpression: item.IsExpression, Checked: false, IsVisible: true
                                }));

            return {...state, fieldsToAdd: [...fieldsToAdd],  fieldsDialogOpen: true};
        },

        closeFieldsDialog: (state, action) => ({...state, fieldsToAdd: [], fieldsDialogOpen: false}),

        setFieldChecked: (state, action) => ({...state, fieldsToAdd:state.fieldsToAdd.map(item =>{
            //payload: FieldId
            if (item.FieldId == action.payload)
                return {...item, Checked: !item.Checked};
            else 
                return {...item};
        })}),

        clearFields: (state, action) => ({ ...state, formValues: {...state.formValues, Fields: []}}),


        //computed field dialog
        openComputedFieldDialog: (state, action) => {
            //payload:{mode:, fieldId:, functionId:, parentFieldId:}
            let mode = action.payload.mode;
            let fieldId = action.payload.fieldId;
            let functionId = action.payload.functionId;
            let parentFieldId = action.payload.parentFieldId;

            const pageState = current(state);       

            return {
                ...state, 
                computedFieldDialogOpen: true, 
                computedFieldFieldId: mode == 1 ? null : fieldId, 
                computedFieldFunctionId: mode == 1 ? 0 : functionId, 
                computedFieldParentFieldId: mode == 1 ? null : parentFieldId, 
                computedFieldMode: mode
            };
        },

        closeComputedFieldDialog: (state, action) => ({
            ...state, 
            computedFieldDialogOpen: false, 
            computedFieldFieldId: null, 
            computedFieldFunctionId: 0, 
            computedFieldParentFieldId: null, 
            computedFieldMode: 0
        }),

        //expression field dialog
        openExpressionFieldDialog: (state, action) => {
            //payload:{mode:, fieldId:, dataTypeId:, isDecimal:, isPercent, body:}
            let mode = action.payload.mode;
            let fieldId = action.payload.fieldId;
            let dataTypeId = action.payload.dataTypeId;
            let isDecimal = action.payload.isDecimal;
            let isPercent = action.payload.isPercent;
            let body = action.payload.body;

            const pageState = current(state);       
            return {
                ...state, 
               expressionFieldDialogOpen: true, 
               expressionFieldFieldId: mode == 1 ? null : fieldId, 
               expressionFieldDataTypeId: mode == 1 ? 0 : dataTypeId, 
               expressionFieldIsDecimal: mode == 1 ? false : isDecimal, 
               expressionFieldIsPercent: mode == 1 ? false : isPercent, 
               expressionFieldBody: mode == 1 ? "" : body,
               expressionFieldMode: mode
            };
        },

        closeExpressionFieldDialog: (state, action) => ({
            ...state, 
            expressionFieldDialogOpen: false, 
            expressionFieldFieldId: null, 
            expressionFieldDataTypeId: 0, 
            expressionFieldIsDecimal: false, 
            expressionFieldIsPercent: false, 
            expressionFieldBody: "", 
            expressionFieldMode: 0        
        }),

        disposePage: (state, action) => {
            //payload: bool  
            if (action.payload)
                return {...initialState};
            else  
                return {...state, pageDisposed: false};
        },
    }
});

export default dataSourcesPageSlice.reducer;


export const loadPage = (t) => async (dispatch, getState) => {    
    //start with initial values
    dispatch(setInitialState());

    //undispose page
    dispatch(disposePage(false));

    //show loader
    dispatch(setProcessing({processing: true, processingText: t("slices.loadingPage")}));

    try{
        var response = await api({            
                url: 'datasource/getdatasourcespagedata',
                method:  'post',
                data: {
                },
                //headers:{'h':'1'}
        });
        
        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().dataSourcesPage.pageDisposed)
           return;
           
        //it's ok to dispatch here
        let data = response.data.Data;

        //transform the response
        //add the 0/None dataset
        data.Datasets.splice(0, 0, {...defaultDataset});
       
        dispatch(pageLoaded(data));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false        
        return Promise.reject(err);
    }
};

export const loadDataset = (datasetId, t) => async (dispatch, getState) => {
    //show loading
    dispatch(setProcessing({processing: true, processingText: t("slices.loadingDataset")}));

    if (datasetId == 0)
    {   
        dispatch(datasetLoaded({datasetId: 0, dataSources: [], fields: [], fieldGroups: []}));
        return;
    }

    try{
        var response = await api({            
            url: "datasource/getdatasourcesbydatasetid",
            method:  'post',
            data: {
                datasetId: datasetId
            },
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().dataSourcesPage.pageDisposed)
           return;

        let data = response.data.Data;

        dispatch(datasetLoaded({
            datasetId: datasetId, 
            dataSources: data.Items,//sorted by title from api
            fields: data.Fields,//sorted by title from api
            fieldGroups: data.FieldGroups//sorted by title from api
        }));
        
        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};

export const loadEntity = (entityId, t) => async (dispatch, getState) => {    
    //show loader
    dispatch(setProcessing({processing: true, processingText: t("slices.loadingDataSource")}));

    try{
        var response = await api({            
            url: "datasource/getdatasourcebyid",
            method:  'post',
            data: {
                id: entityId
            },
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().dataSourcesPage.pageDisposed)
           return;

        let data = response.data.Data;

        //transform data
        //set aggregate function id = 1000 when is grouped 
        //removealso set aggregate title = title
        if (data.Fields && data.Fields.length > 0)
        {
            for (let i in data.Fields)
            {
                if (data.Fields[i].IsGrouped)
                    data.Fields[i].FunctionId = 1000;
            }
        }

        dispatch(entityLoaded(data));
        
        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};

export const saveEntity = (t) => async (dispatch, getState) => {
    const pageState = getState().dataSourcesPage;
    
    let entityId = pageState.formValues.Id;
    let url = pageState.mode == 1 ? "datasource/createdatasource" : "datasource/updatedatasource";
    let title = pageState.mode == 1 ? t("slices.creatingDataSource") : t("slices.updatingDataSource");

    //show loader
    dispatch(setProcessing({processing: true, processingText: title}));

    //must pass IsImplicit which is always false
    let entity = {...pageState.formValues, IsImplicit: false};
    
    //transform 
    //the create/update apis don't accept FunctionId; rather, they accept AggregateFunctionId and ComputeFunctionId
    //also, we must don't pass DataTypeId and IsDecimal and IsPercent; rather we pass ExpressionBodyDataType only for the DT of expression field and same for decimal and percent
    //also need to transform functionid=1000 to 0; IsGrouped is already handled here
    //when aggregate function id is 1000, make it 0
    if (entity.Fields && entity.Fields.length > 0)
    {
        entity = {...entity, Fields: entity.Fields.map(item =>{
            let tempItem = {...item};

            if (tempItem.IsComputedField)
            {
                if (tempItem.FunctionId > 0)
                {
                    //cff
                    tempItem.AggregateFunctionId = 0;
                    tempItem.ComputeFunctionId = tempItem.FunctionId;
                    tempItem.HasExpressionBody = false;
                    tempItem.ExpressionDataTypeId = 0;
                    tempItem.ExpressionIsDecimal = false;
                    tempItem.ExpressionIsPercent = false;
                }
                else
                {
                    //exp.
                    tempItem.AggregateFunctionId = 0;
                    tempItem.ComputeFunctionId = 0;
                    tempItem.HasExpressionBody = true;
                    tempItem.ExpressionDataTypeId = tempItem.DataTypeId;
                    tempItem.ExpressionIsDecimal = tempItem.IsDecimal;
                    tempItem.ExpressionIsPercent = tempItem.IsPercent;
                }
            }
            else
            {
                tempItem.AggregateFunctionId = (tempItem.FunctionId == 1000) ? 0 : tempItem.FunctionId;
                tempItem.ComputeFunctionId = 0;
                tempItem.HasExpressionBody = false;
                tempItem.ExpressionDataTypeId = 0;
                tempItem.ExpressionIsDecimal = false;
                tempItem.ExpressionIsPercent = false;
            }

            return tempItem;
        })};
    }

    try{
        var response = await api({            
            url:url,
            method:  'post',
            data: {...entity },
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().dataSourcesPage.pageDisposed)
           return;

        let data = response.data.Data;

        if (pageState.mode == 1)
            dispatch(entityInserted({id: data.Id, fields: data.Fields}));
        else
            dispatch(entityUpdated({fields: data.Fields}));
        
        let successMessage = pageState.mode == 1 ? t("slices.createdDataSource") : t("slices.updatedDataSource");

        dispatch(addNotification({text: successMessage, type: "success", closable: true, duration: notificationDurationMiddle, position:"topRight"}));

        dispatch(setProcessing({processing: false}));

        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};

export const deleteEntity = (id, t) => async (dispatch, getState) => {
    //payload the entity id
    dispatch(setProcessing({processing: true, processingText: t("slices.deletingDataSource")}));

    try{
        var response = await api({            
            url:"/datasource/removedatasource",
            method:  'post',
            data: {id: id},
            //headers:{'h':'1'}
        });

        //i am here +. i can safely access response.data.Succeeded which is always true and response.data.Data
        if(getState().dataSourcesPage.pageDisposed)
           return;

        let data = response.data.Data;

        dispatch(addNotification({text: t("slices.deletedDataSource"), type: "success", closable: true, duration: notificationDurationMiddle, position:"topRight"}));

        dispatch(entityDeleted(id));

        dispatch(setProcessing({processing: false}));
        
        return Promise.resolve(response);
    }
    catch(err)
    {
        //i am here => could be any error included 200 and Succeeded=false
        return Promise.reject(err);
    }
};


export const { setInitialState, setProcessing, setPageValue, pageLoaded, setPanes, datasetLoaded, createEntity, setFormValue, setFormServerErrors, entityLoaded, removeEntityFromUI, entityInserted, entityUpdated, entityCloned, entityDeleted, openFieldsDialog, closeFieldsDialog, setFieldChecked, clearFields, setFieldValue, openComputedFieldDialog, closeComputedFieldDialog, openExpressionFieldDialog, closeExpressionFieldDialog, disposePage } = dataSourcesPageSlice.actions;

